import React, { useCallback, useState } from 'react';
import LoaderOverlay from './LoaderOverlay';
import { showAppAlert } from '../utils/alertService';

const API_URL = process.env.REACT_APP_API_BASE_URL;

const sortableHeaderStyle = {
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
  font: 'inherit',
  fontWeight: 'bold',
  padding: 0,
};

function formatDate(date) {
  if (!date) return '-';
  const [year, month, day] = date.split('-');
  return year && month && day ? `${day}/${month}/${year}` : date;
}

function sortRows(rows, sortConfig, valueGetter) {
  return [...rows].sort((a, b) => {
    const valueA = valueGetter(a, sortConfig.key);
    const valueB = valueGetter(b, sortConfig.key);

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
    }

    return sortConfig.direction === 'asc'
      ? String(valueA).localeCompare(String(valueB))
      : String(valueB).localeCompare(String(valueA));
  });
}

function CompanyMovements({ lojistas = [] }) {
  const [companyId, setCompanyId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [movements, setMovements] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [movementSort, setMovementSort] = useState({ key: 'data_envio', direction: 'desc' });
  const [compositionSort, setCompositionSort] = useState({ key: 'total', direction: 'desc' });
  const token = localStorage.getItem('token');

  const consolidatedMovements = movements.reduce((acc, movement) => {
    const emailType = movement.tipo_email || 'Nao informado';
    const total = Number(movement.Total_emails_enviados || 0);
    acc[emailType] = (acc[emailType] || 0) + total;
    return acc;
  }, {});
  const totalMovements = Object.values(consolidatedMovements).reduce((acc, total) => acc + total, 0);
  const consolidatedRows = Object.entries(consolidatedMovements).map(([emailType, total]) => ({
    emailType,
    total,
    percentage: totalMovements > 0 ? (total / totalMovements) * 100 : 0,
  }));

  const sortedMovements = sortRows(movements, movementSort, (movement, key) => {
    if (key === 'total') return Number(movement.Total_emails_enviados || 0);
    return movement[key] || '';
  });
  const sortedConsolidatedRows = sortRows(consolidatedRows, compositionSort, (row, key) => row[key] || 0);

  const handleSort = (setSortConfig, key) => {
    setSortConfig((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortLabel = (label, sortConfig, key) => {
    if (sortConfig.key !== key) return label;
    return `${label} ${sortConfig.direction === 'asc' ? '▲' : '▼'}`;
  };

  const fetchCompanyMovements = useCallback(async () => {
    if (!companyId) {
      showAppAlert('info', 'Selecione um lojista para buscar as movimentacoes.');
      return;
    }
    if (!startDate || !endDate) {
      showAppAlert('info', 'Selecione um periodo para buscar as movimentacoes.');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      showAppAlert('warning', 'A data de fim nao pode ser anterior a data de inicio.');
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams({
        dataInicio: startDate,
        dataFim: endDate,
      });
      const response = await fetch(`${API_URL}/admin/custommerMovements/${companyId}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (response.ok && Array.isArray(data)) {
        setMovements(data);
      } else {
        setMovements([]);
        showAppAlert('error', data.message || data.error || 'Erro ao buscar movimentacoes dos lojistas.');
      }
    } catch (error) {
      setMovements([]);
      showAppAlert('error', 'Ocorreu um erro de conexao ao buscar as movimentacoes dos lojistas.');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, endDate, startDate, token]);

  return (
    <section className="dashboard-section">
      <LoaderOverlay isLoading={isLoading} message="Buscando movimentacoes dos lojistas..." />
      <h3>Movimenta&ccedil;&atilde;o dos Lojistas</h3>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <div className="filter-group">
          <label htmlFor="companyMovementsCompanyId">Lojista:</label><br />
          <select
            id="companyMovementsCompanyId"
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              setMovements([]);
              setHasSearched(false);
            }}
            className="input-style"
          >
            <option value="">Selecione um Lojista</option>
            {lojistas.map((lojista, index) => {
              const optionCompanyId = lojista.companyId || lojista.id;
              return (
                <option key={optionCompanyId || index} value={optionCompanyId || ''}>
                  {lojista.nomeFantasia || lojista.email || `Lojista ${index + 1}`}
                </option>
              );
            })}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="companyMovementsStartDate">Data de Inicio:</label><br />
          <input
            type="date"
            id="companyMovementsStartDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="input-style"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="companyMovementsEndDate">Data de Fim:</label><br />
          <input
            type="date"
            id="companyMovementsEndDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="input-style"
            disabled={!startDate}
            min={startDate}
          />
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button onClick={fetchCompanyMovements} className="button-style" disabled={isLoading || !companyId || !startDate || !endDate}>
            {isLoading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {movements.length > 0 ? (
        <>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>
                  <button type="button" style={sortableHeaderStyle} onClick={() => handleSort(setMovementSort, 'tipo_email')}>
                    {getSortLabel('Tipo de E-mail', movementSort, 'tipo_email')}
                  </button>
                </th>
                <th>
                  <button type="button" style={sortableHeaderStyle} onClick={() => handleSort(setMovementSort, 'total')}>
                    {getSortLabel('Total de E-mails Enviados', movementSort, 'total')}
                  </button>
                </th>
                <th>
                  <button type="button" style={sortableHeaderStyle} onClick={() => handleSort(setMovementSort, 'data_envio')}>
                    {getSortLabel('Data de Envio', movementSort, 'data_envio')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedMovements.map((movement, index) => (
                <tr key={`${movement.tipo_email}-${movement.data_envio}-${index}`}>
                  <td>{movement.tipo_email}</td>
                  <td>{movement.Total_emails_enviados}</td>
                  <td>{formatDate(movement.data_envio)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td>{totalMovements}</td>
                <td>-</td>
              </tr>
            </tfoot>
          </table>

          <section className="dashboard-section" style={{ marginTop: '30px' }}>
            <h3>Composicao por tipo de email</h3>
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" style={sortableHeaderStyle} onClick={() => handleSort(setCompositionSort, 'emailType')}>
                      {getSortLabel('Tipo email', compositionSort, 'emailType')}
                    </button>
                  </th>
                  <th>
                    <button type="button" style={sortableHeaderStyle} onClick={() => handleSort(setCompositionSort, 'total')}>
                      {getSortLabel('Volume', compositionSort, 'total')}
                    </button>
                  </th>
                  <th>
                    <button type="button" style={sortableHeaderStyle} onClick={() => handleSort(setCompositionSort, 'percentage')}>
                      {getSortLabel('% Total', compositionSort, 'percentage')}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedConsolidatedRows.map((row) => (
                  <tr key={row.emailType}>
                    <td>{row.emailType}</td>
                    <td>{row.total}</td>
                    <td>{`${row.percentage.toFixed(2)}%`}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td>{totalMovements}</td>
                  <td>{totalMovements > 0 ? '100.00%' : '0.00%'}</td>
                </tr>
              </tfoot>
            </table>
          </section>
        </>
      ) : (
        <p>{hasSearched ? 'Sem Informações no momento' : 'Selecione um lojista e clique em Buscar para ver os dados.'}</p>
      )}
    </section>
  );
}

export default CompanyMovements;
